import { getLocale } from '@rhino-project/core/i18n';
import { useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { RhinoAttribute } from '@rhino-project/core';
import { dateFilterOperatorLabel, useModelFilterField } from '../../../form';
import { useModelFiltersContext } from '@rhino-project/core/hooks';
import { FilterDate } from '../../../Filter';
import { DateFormatter, parseDate } from '@internationalized/date';

const FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short', // Short month name (e.g., Feb)
  day: 'numeric', // Numeric day (e.g., 4)
  year: 'numeric' // Numeric year (e.g., 2025)
};

const buildDateTimePill = (
  attribute: RhinoAttribute,
  operator: string,
  newValue: string
) => {
  if (!newValue) return null;

  const locale = getLocale().replaceAll('_', '-');
  const formatter = new DateFormatter(locale, FORMAT_OPTIONS);

  // The original calls toDate() with no timezone argument; preserve verbatim.
  const date = (parseDate(newValue) as unknown as { toDate(): Date }).toDate();

  return `${attribute.readableName} ${dateFilterOperatorLabel(
    attribute.format as string,
    operator
  )} ${formatter.format(date)}`;
};

export const ModelFilterDate = ({
  path,
  ...props
}: {
  path: string;
  [key: string]: unknown;
}) => {
  const { attribute, operator, operatorPath, ...rest } =
    useModelFilterField(path);

  const minValue = useMemo(() => {
    if (!attribute.minimum) return undefined;

    const date = parseDate(attribute.minimum as unknown as string);

    // If the minimum is exclusive, we need to add a day to it so the value is not included
    if (attribute.exclusiveMinimum) return date.add({ days: 1 });

    return date;
  }, [attribute]);

  const maxValue = useMemo(() => {
    if (!attribute.maximum) return undefined;

    const date = parseDate(attribute.maximum as unknown as string);

    // If the maximum is exclusive, we need to subtract a millisecond to it so the value is not included
    if (attribute.exclusiveMaximum) return date.subtract({ days: 1 });

    return date;
  }, [attribute]);

  const watch = useWatch({ name: operatorPath });

  const { setPill } = useModelFiltersContext();

  useEffect(() => {
    if (watch !== undefined && watch !== '')
      setPill(operatorPath, buildDateTimePill(attribute, operator, watch));
  }, [attribute, operator, operatorPath, setPill, watch]);

  return (
    <FilterDate
      path={operatorPath}
      granularity="day"
      parse={parseDate}
      minValue={minValue}
      maxValue={maxValue}
      {...(rest as Record<string, unknown>)}
      {...props}
    />
  );
};
